import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/components/auth/AuthProvider";
import { ProviderType } from "@/types/booking";

interface ProfileInfo {
  id: string;
  name: string;
  type: ProviderType;
  dashboardPath: string;
}

interface UserProfiles {
  trainer: ProfileInfo | null;
  nutritionist: ProfileInfo | null;
  physiotherapist: ProfileInfo | null;
  businesses: ProfileInfo[];
  loading: boolean;
  hasAnyProfile: boolean;
  primaryProfile: ProfileInfo | null;
}

const BUSINESS_DASHBOARD_PATHS: Record<string, string> = {
  yoga: "/dashboard/yoga/reservas",
  crossfit: "/dashboard/crossfit/reservas",
  gym: "/dashboard/gym/reservas",
  pilates: "/dashboard/pilates/reservas",
  dance: "/dashboard/dance/reservas",
  martial_arts: "/dashboard/martial-arts/reservas",
};

export function useUserProfiles(): UserProfiles {
  const { user } = useAuth();
  const [trainer, setTrainer] = useState<ProfileInfo | null>(null);
  const [nutritionist, setNutritionist] = useState<ProfileInfo | null>(null);
  const [physiotherapist, setPhysiotherapist] = useState<ProfileInfo | null>(null);
  const [businesses, setBusinesses] = useState<ProfileInfo[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user) {
      setTrainer(null);
      setNutritionist(null);
      setPhysiotherapist(null);
      setBusinesses([]);
      setLoading(false);
      return;
    }

    const fetchProfiles = async () => {
      setLoading(true);
      
      try {
        // Fetch all profiles in parallel
        const [trainerRes, nutritionistRes, physiotherapistRes, businessRes] = await Promise.all([
          supabase
            .from("trainer_profiles")
            .select("id, full_name")
            .eq("user_id", user.id)
            .limit(1)
            .maybeSingle(),
          supabase
            .from("nutritionist_profiles")
            .select("id, full_name")
            .eq("user_id", user.id)
            .limit(1)
            .maybeSingle(),
          supabase
            .from("physiotherapist_profiles")
            .select("id, full_name")
            .eq("user_id", user.id)
            .limit(1)
            .maybeSingle(),
          supabase
            .from("business_profiles")
            .select("id, business_name, business_type")
            .eq("user_id", user.id),
        ]);

        // Set trainer profile
        if (trainerRes.data) {
          setTrainer({
            id: trainerRes.data.id,
            name: trainerRes.data.full_name || "Entrenador",
            type: "trainer",
            dashboardPath: "/dashboard/reservas",
          });
        } else {
          setTrainer(null);
        }

        // Set nutritionist profile
        if (nutritionistRes.data) {
          setNutritionist({
            id: nutritionistRes.data.id,
            name: nutritionistRes.data.full_name || "Nutricionista",
            type: "nutritionist",
            dashboardPath: "/dashboard/nutricionista/reservas",
          });
        } else {
          setNutritionist(null);
        }

        // Set physiotherapist profile
        if (physiotherapistRes.data) {
          setPhysiotherapist({
            id: physiotherapistRes.data.id,
            name: physiotherapistRes.data.full_name || "Fisioterapeuta",
            type: "physiotherapist",
            dashboardPath: "/dashboard/fisioterapeuta/reservas",
          });
        } else {
          setPhysiotherapist(null);
        }

        // Set business profiles
        if (businessRes.data && businessRes.data.length > 0) {
          const businessProfiles = businessRes.data.map((b) => ({
            id: b.id,
            name: b.business_name || b.business_type,
            type: b.business_type as ProviderType,
            dashboardPath: BUSINESS_DASHBOARD_PATHS[b.business_type] || "/dashboard",
          }));
          setBusinesses(businessProfiles);
        } else {
          setBusinesses([]);
        }
      } catch (error) {
        console.error("Error fetching user profiles:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchProfiles();
  }, [user]);

  const hasAnyProfile = !!(trainer || nutritionist || physiotherapist || businesses.length > 0);
  
  // Primary profile prioritization: trainer > nutritionist > physiotherapist > first business
  const primaryProfile = trainer || nutritionist || physiotherapist || businesses[0] || null;

  return {
    trainer,
    nutritionist,
    physiotherapist,
    businesses,
    loading,
    hasAnyProfile,
    primaryProfile,
  };
}
